import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  input,
  Output,
  TemplateRef,
  ViewChildren,
  QueryList,
  AfterViewInit,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { DxFormModule, DxValidationGroupModule, DxValidationGroupComponent } from 'devextreme-angular';
import { ClickEvent } from 'devextreme/ui/button';
import { IconName } from 'src/app/helpers/icon-exports';
import { GpdButtonComponent } from '../gpd-button/gpd-button.component';
import { GpdStepperComponent, StepperItem, StepperNavigationEvent } from '../gpd-stepper/gpd-stepper.component';
import { DxMultiViewComponent, DxiMultiViewItemComponent } from 'devextreme-angular/ui/multi-view';

export interface Step {
  id: string;
  title: string;
  icon: IconName;
  description?: string;
  template: TemplateRef<any>;
  isValid?: boolean;
  disabled?: boolean;
}

export interface StepNavigatedEvent {
  toStep?: Step;
  fromStep: Step;
  clickEvent: ClickEvent;
}

@Component({
    selector: 'gpd-multi-step-form',
    imports: [
        CommonModule,
        DxFormModule,
        GpdButtonComponent,
        DxValidationGroupModule,
        GpdStepperComponent,
        DxMultiViewComponent,
        DxiMultiViewItemComponent,
        DxFormModule,
    ],
    templateUrl: './multi-step-form.component.html',
    styleUrl: './multi-step-form.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiStepFormComponent implements AfterViewInit, OnDestroy {
  title = input.required<string>();
  steps = input.required<Step[]>();

  finishText = input<string>($localize`:@@finish:Finish`);

  stepperItems = computed(() => {
    return this.steps()?.reduce((acc, step, index) => {
      let disabled = false;

      // Disable step if previous step is invalid or disabled
      if (index > 0) {
        const previousStepperItem = acc[index - 1];
        if (previousStepperItem.disabled || !this.steps()[index - 1].isValid) {
          disabled = true;
        }
      }

      acc.push({ ...step, disabled: disabled });

      return acc;
    }, [] as StepperItem[]);
  });
  @Output() onNextClicked = new EventEmitter<StepNavigatedEvent>();
  @Output() onPreviousClicked = new EventEmitter<StepNavigatedEvent>();
  @Output() onCancelClicked = new EventEmitter<void>();
  @Output() onFinishClicked = new EventEmitter<StepNavigatedEvent>();

  // State
  currentStepIndex = signal(0);
  currentStepIsValid = signal(true);

  @ViewChild(DxMultiViewComponent)
  multiView!: DxMultiViewComponent;

  @ViewChildren(DxValidationGroupComponent)
  validationGroups!: QueryList<DxValidationGroupComponent>;

  // Computed properties for button states
  isNextButtonDisabled = computed(() => !this.currentStepIsValid());
  isFinishButtonDisabled = computed(() => !this.currentStepIsValid());

  // Computed property for current step
  currentStep = computed(() => this.steps()[this.currentStepIndex()] || null);

  // Validation monitoring
  private validationInterval?: number;
  private lastValidationState?: boolean;

  constructor() {}

  ngAfterViewInit() {
    // Set up validation monitoring after view initialization
    this.setupValidationMonitoring();
    // Validate initial step
    this.updateCurrentStepValidationState();
  }

  ngOnDestroy() {
    // Clean up validation monitoring
    this.cleanupValidationMonitoring();
  }

  onCancelBtnClicked(e: ClickEvent) {
    this.onCancelClicked.emit();
  }

  onNextBtnClicked(e: ClickEvent) {
    const isValid = this.validateCurrentStep();

    if (!isValid) {
      return; // Don't proceed if validation fails
    }

    const nextIndex = this.currentStepIndex() + 1;
    if (nextIndex < this.steps().length) {
      this.currentStepIndex.set(nextIndex);
      // Reset validation monitoring for new step
      this.cleanupValidationMonitoring();
      this.setupValidationMonitoring();
      this.updateCurrentStepValidationState();

      this.onNextClicked.emit({
        toStep: this.steps()[nextIndex],
        fromStep: this.steps()[nextIndex - 1],
        clickEvent: e,
      });
    }
  }

  onPreviousBtnClicked(e: ClickEvent) {
    const prevIndex = this.currentStepIndex() - 1;
    if (prevIndex >= 0) {
      this.currentStepIndex.set(prevIndex);
      // Reset validation monitoring for new step
      this.cleanupValidationMonitoring();
      this.setupValidationMonitoring();
      this.updateCurrentStepValidationState();

      this.onPreviousClicked.emit({
        toStep: this.steps()[prevIndex],
        fromStep: this.steps()[prevIndex + 1],
        clickEvent: e,
      });
    }
  }

  onFinishBtnClicked(e: ClickEvent) {
    const isValid = this.validateCurrentStep();

    if (!isValid) {
      return; // Don't proceed if validation fails
    }

    this.onFinishClicked.emit({
      toStep: undefined,
      fromStep: this.currentStep(),
      clickEvent: e,
    });
  }

  /**
   * Validates the current step using DevExtreme's ValidationGroup API
   */
  validateCurrentStep(): boolean {
    const currentStep = this.currentStep();
    if (!currentStep) {
      return true;
    }

    const validationGroup = this.findValidationGroup(currentStep.id);
    if (!validationGroup) {
      return true; // No validation group means no validation required
    }

    try {
      const result = validationGroup.instance.validate();
      return result.isValid;
    } catch (error) {
      // Log error but allow progression to avoid blocking user
      console.warn(`Validation error for step ${currentStep.id}:`, error);
      return true;
    }
  }

  /**
   * Finds the validation group for a specific step ID
   */
  private findValidationGroup(stepId: string): DxValidationGroupComponent | undefined {
    return this.validationGroups?.find(group => group.instance?.element()?.id === `validation-group-${stepId}`);
  }

  onStepperSelectionChanged(event: StepperNavigationEvent) {
    // Only allow forward navigation if current step is valid
    if (event.currentIndex > this.currentStepIndex() && !this.validateCurrentStep()) {
      return; // Block navigation if validation fails
    }

    this.currentStepIndex.set(event.currentIndex);
    // Reset validation monitoring for new step
    this.cleanupValidationMonitoring();
    this.setupValidationMonitoring();
    this.updateCurrentStepValidationState();
  }

  /**
   * Updates the validation state for the current step
   */
  updateCurrentStepValidationState(): void {
    const isValid = this.validateCurrentStep();
    this.currentStepIsValid.set(isValid);
  }

  /**
   * Public method to trigger validation update from external components
   * This is called when data changes that may affect validation
   */
  public triggerValidationUpdate(): void {
    this.updateCurrentStepValidationState();
  }

  /**
   * Public method to reset validation for dynamic form changes
   * This is useful when new form inputs are dynamically added
   */
  public resetValidation(): void {
    // DevExtreme automatically handles validation group updates
    // Just trigger a validation state update
    this.updateCurrentStepValidationState();
  }

  /**
   * Sets up validation monitoring to automatically update button states
   */
  private setupValidationMonitoring(): void {
    // Check validation state every 200ms to catch changes
    this.validationInterval = window.setInterval(() => {
      const currentValidationState = this.validateCurrentStep();

      // Only update if validation state changed to avoid unnecessary updates
      if (this.lastValidationState !== currentValidationState) {
        this.lastValidationState = currentValidationState;
        this.currentStepIsValid.set(currentValidationState);
      }
    }, 200);
  }

  /**
   * Cleans up validation monitoring
   */
  private cleanupValidationMonitoring(): void {
    if (this.validationInterval) {
      clearInterval(this.validationInterval);
      this.validationInterval = undefined;
    }
  }

  calculateMFPercentage(): number {
    return Math.floor((this.currentStepIndex() / this.steps().length) * 100);
  }
}
